import { MessageSquareIcon } from 'lucide-react'

import { Alert } from '@/gen/inventory/v1/alert_pb'
import { Control, SmartQuestionStatus } from '@/gen/inventory/v1/control_service_pb'
import { OngoingAssessment } from '@/gen/inventory/v1/ongoing_assessment_pb'
import { QuestionnaireStatus } from '@/gen/questionnaire/common/v1/questionnaire_status_pb'
import { Questionnaire, QuestionnaireType } from '@/gen/questionnaire/v2/model_pb'

import { SECTION_LABELS } from '@/const/label'
import { sortControlStatus } from '@/const/priority'

import { useTrackCallback } from '@/lib/analytics/events'
import { isUnvalidatedControlStatus } from '@/lib/control-status'
import { UrlParam, useUrlParams } from '@/lib/url/url-param.hook'

import { countOpenOrOngoingAlerts } from '@/pages/alert-inventory/utils'
import { useAssessmentAnalytics } from '@/pages/assessment/analytics'
import { ControlWeightLegend } from '@/pages/assessment/assessment-tabs/assessment-overview-tab/control-weight-legend'
import { ASSESSMENT_TABS } from '@/pages/assessment/assessment-tabs/assessment-tabs'

import { Badge } from '@/components/lemmala-design-system/badge/badge'
import { Separator } from '@/components/lemmala-design-system/separator/separator'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'

import { ControlWeightPieChart } from './control-weight-pie-chart'
import { OverviewCard } from './overview-card'

type IssuesCardProps = {
  companyId: string
  alerts: Alert[]
  controls: Control[]
  questionnaires: Questionnaire[]
  assessmentScore: OngoingAssessment['score']
}

export const IssuesCard = ({
  alerts,
  controls,
  questionnaires,
  assessmentScore,
}: IssuesCardProps) => {
  const trackingContext = useAssessmentAnalytics()
  const trackIssuesClick = useTrackCallback('third-party.assessment.overview.issues.click')
  const {
    unvalidatedControlsCount,
    awaitingRecipientControlsCount,
    awaitingReviewControlsCount,
    awaitingRecipientCommentsCount,
    awaitingReviewCommentsCount,
  } = controls
    .filter((control) => isUnvalidatedControlStatus(control.status))
    .reduce(
      (acc, control) => {
        acc.unvalidatedControlsCount++

        switch (control.smartQuestion?.status) {
          case SmartQuestionStatus.AWAITING_RECIPIENT:
            acc.awaitingRecipientControlsCount++
            acc.awaitingRecipientCommentsCount += control.smartQuestion?.commentCount || 0
            break
          case SmartQuestionStatus.AWAITING_REVIEW:
            acc.awaitingReviewControlsCount++
            acc.awaitingReviewCommentsCount += control.smartQuestion?.commentCount || 0
            break
          default:
            break
        }

        return acc
      },
      {
        unvalidatedControlsCount: 0,
        awaitingRecipientControlsCount: 0,
        awaitingReviewControlsCount: 0,
        awaitingRecipientCommentsCount: 0,
        awaitingReviewCommentsCount: 0,
      },
    )

  const isSmartEvidenceRequestPublished = questionnaires.some(
    (questionnaire) =>
      questionnaire?.base?.status === QuestionnaireStatus.ONGOING &&
      questionnaire?.base?.type === QuestionnaireType.RISK,
  )

  const openAlertsCount = countOpenOrOngoingAlerts(alerts)
  const totalIssues = unvalidatedControlsCount + openAlertsCount
  const { updateParam } = useUrlParams()
  const actionLabel =
    totalIssues === 0 && controls.length > 0 ? 'Conclude assessment' : 'Manage issues'
  const actionTarget = totalIssues === 0 ? ASSESSMENT_TABS.CONCLUSION : ASSESSMENT_TABS.ISSUES

  const smartEvidenceDetails = [
    <div key='title' className='text-nowrap text-main-foreground-tertiary text-body-default'>
      Evidence request
    </div>,
    !isSmartEvidenceRequestPublished && (
      <Badge key='not-published' variant='neutral'>
        Not published
      </Badge>
    ),
    awaitingRecipientControlsCount > 0 && (
      <div key='awaiting-recipient' className='flex items-center gap-2 text-label-small'>
        <div>{awaitingRecipientControlsCount} Awaiting recipient</div>
        {awaitingRecipientCommentsCount > 0 && (
          <Badge variant='neutral'>
            <TextWithIcon
              text={awaitingRecipientCommentsCount}
              icon={<MessageSquareIcon size={14} />}
            />
          </Badge>
        )}
      </div>
    ),
    awaitingReviewControlsCount > 0 && (
      <div key='awaiting-review' className='flex items-center gap-2 text-label-small'>
        <div>{awaitingReviewControlsCount} Awaiting reviewer</div>
        {awaitingReviewCommentsCount > 0 && (
          <Badge variant='neutral'>
            <TextWithIcon
              text={awaitingReviewCommentsCount}
              icon={<MessageSquareIcon size={14} />}
            />
          </Badge>
        )}
      </div>
    ),
  ].filter(Boolean)

  return (
    <OverviewCard
      title='Issues'
      details={
        <div className='flex items-start justify-between gap-6'>
          <div className='flex-shrink-0'>
            {controls.length > 0 ? (
              <div className='flex gap-6'>
                <ControlWeightPieChart
                  controls={controls.sort((a, b) => sortControlStatus(a.status, b.status))}
                />
                <ControlWeightLegend assessmentScore={assessmentScore} controls={controls} />
              </div>
            ) : (
              <div className='flex w-52 flex-col'>
                <div className='text-main-foreground-primary text-heading-xlarge'>0</div>
                <div className='my-6 text-main-foreground-tertiary text-body-small'>
                  No controls assigned
                </div>
              </div>
            )}
          </div>
          {smartEvidenceDetails.length > 0 && (
            <>
              <Separator className='self-stretch' orientation='vertical' />
              <div className='flex flex-1 flex-col items-start gap-2'>{smartEvidenceDetails}</div>
            </>
          )}
          {openAlertsCount > 0 && (
            <div className='ml-10 flex size-36 min-w-36 flex-col items-center justify-center rounded-2xl bg-muted text-main-foreground-danger'>
              <div className='text-heading-large'>{openAlertsCount}</div>
              <div className='text-label-small'>{SECTION_LABELS.alerts}</div>
            </div>
          )}
        </div>
      }
      buttonClassName='w-fit'
      action={{
        label: actionLabel,
        onClick:
          controls.length > 0 || totalIssues > 0
            ? () => {
                if (trackingContext) {
                  trackIssuesClick({
                    ...trackingContext,
                    label:
                      actionLabel === 'Manage issues' ? 'manage-issues' : 'conclude-assessment',
                    issueCount: totalIssues,
                  })
                }

                updateParam(UrlParam.ASSESSMENT_TAB, actionTarget)
              }
            : undefined,
      }}
    />
  )
}
